"use client";

import type { CreatorQuestion } from "@/features/quiz/types";

export const QUIZ_CREATOR_DRAFT_NAMESPACE = "quiz_creator_draft";
const QUIZ_CREATOR_CONTEXT_NAMESPACE = "quiz_creator_draft_context";
/**
 * Bump whenever the persisted form state changes shape, so a stale draft is
 * discarded rather than restored half-populated. v2: `securityConfig` fields
 * (`fullscreenRequired` / `tabSwitchMode` / `blockCopyPaste`) became the
 * `proctorConfig` axes (EX-INT01).
 */
const QUIZ_CREATOR_DRAFT_VERSION = 2;
const QUIZ_CREATOR_DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000;

export type QuizCreatorAiPreviewDraft = {
  questions: CreatorQuestion[];
  selectedKeys: string[];
  source: "topic" | "text" | "document";
  savedAt: number;
};

export type QuizCreatorDraftPayload<TFormState> = {
  version: number;
  savedAt: number;
  formState?: TFormState;
  aiPreview?: QuizCreatorAiPreviewDraft;
};

function hasStorage(): boolean {
  return typeof window !== "undefined" && typeof window.localStorage !== "undefined";
}

export function getQuizCreatorDraftKey(userId: string, quizContextId: string): string {
  return `${QUIZ_CREATOR_DRAFT_NAMESPACE}:${userId}:${quizContextId}`;
}

function getClientContextKey(userId: string): string {
  return `${QUIZ_CREATOR_CONTEXT_NAMESPACE}:${userId}:new`;
}

function safeParseDraft<TFormState>(
  raw: string | null,
): QuizCreatorDraftPayload<TFormState> | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<QuizCreatorDraftPayload<TFormState>>;
    if (parsed.version !== QUIZ_CREATOR_DRAFT_VERSION || typeof parsed.savedAt !== "number") {
      return null;
    }
    return parsed as QuizCreatorDraftPayload<TFormState>;
  } catch {
    return null;
  }
}

function removeExpiredDraft(key: string, savedAt: number): boolean {
  if (Date.now() - savedAt <= QUIZ_CREATOR_DRAFT_TTL_MS) return false;
  window.localStorage.removeItem(key);
  return true;
}

function listDraftKeysForUser(userId?: string): string[] {
  if (!hasStorage()) return [];
  const prefix = userId
    ? `${QUIZ_CREATOR_DRAFT_NAMESPACE}:${userId}:`
    : `${QUIZ_CREATOR_DRAFT_NAMESPACE}:`;
  const keys: string[] = [];
  for (let i = 0; i < window.localStorage.length; i += 1) {
    const key = window.localStorage.key(i);
    if (key?.startsWith(prefix)) keys.push(key);
  }
  return keys;
}

function evictOldestDrafts(userId?: string): void {
  const entries = listDraftKeysForUser(userId)
    .map((key) => {
      const draft = safeParseDraft<unknown>(window.localStorage.getItem(key));
      return { key, savedAt: draft?.savedAt ?? 0 };
    })
    .sort((a, b) => a.savedAt - b.savedAt);

  for (const entry of entries.slice(0, Math.max(1, Math.ceil(entries.length / 3)))) {
    window.localStorage.removeItem(entry.key);
  }
}

function writeDraft<TFormState>(
  userId: string,
  quizContextId: string,
  draft: QuizCreatorDraftPayload<TFormState>,
): void {
  if (!hasStorage()) return;
  const key = getQuizCreatorDraftKey(userId, quizContextId);
  try {
    window.localStorage.setItem(key, JSON.stringify(draft));
  } catch (error) {
    if (error instanceof DOMException && error.name === "QuotaExceededError") {
      evictOldestDrafts(userId);
      try {
        window.localStorage.setItem(key, JSON.stringify(draft));
      } catch {
        // Local recovery is best-effort; never break the creator form.
      }
    }
  }
}

export function loadQuizCreatorDraft<TFormState>(
  userId: string | undefined,
  quizContextId: string | undefined,
): QuizCreatorDraftPayload<TFormState> | null {
  if (!hasStorage() || !userId || !quizContextId) return null;
  const key = getQuizCreatorDraftKey(userId, quizContextId);
  const draft = safeParseDraft<TFormState>(window.localStorage.getItem(key));
  if (!draft) {
    window.localStorage.removeItem(key);
    return null;
  }
  if (removeExpiredDraft(key, draft.savedAt)) return null;
  return draft;
}

export function saveQuizCreatorDraft<TFormState>(
  userId: string | undefined,
  quizContextId: string | undefined,
  next: Omit<QuizCreatorDraftPayload<TFormState>, "version" | "savedAt">,
): void {
  if (!userId || !quizContextId) return;
  writeDraft(userId, quizContextId, {
    ...next,
    version: QUIZ_CREATOR_DRAFT_VERSION,
    savedAt: Date.now(),
  });
}

export function patchQuizCreatorDraft<TFormState>(
  userId: string | undefined,
  quizContextId: string | undefined,
  patch: Partial<Omit<QuizCreatorDraftPayload<TFormState>, "version" | "savedAt">>,
): void {
  if (!userId || !quizContextId) return;
  const current = loadQuizCreatorDraft<TFormState>(userId, quizContextId);
  saveQuizCreatorDraft(userId, quizContextId, {
    ...(current?.formState ? { formState: current.formState } : {}),
    ...(current?.aiPreview ? { aiPreview: current.aiPreview } : {}),
    ...patch,
  });
}

export function clearQuizCreatorDraft(userId: string | undefined, quizContextId: string | undefined): void {
  if (!hasStorage() || !userId || !quizContextId) return;
  window.localStorage.removeItem(getQuizCreatorDraftKey(userId, quizContextId));
}

export function clearQuizCreatorAiPreview<TFormState>(
  userId: string | undefined,
  quizContextId: string | undefined,
): void {
  if (!userId || !quizContextId) return;
  const current = loadQuizCreatorDraft<TFormState>(userId, quizContextId);
  if (!current?.formState) {
    clearQuizCreatorDraft(userId, quizContextId);
    return;
  }
  saveQuizCreatorDraft(userId, quizContextId, { formState: current.formState });
}

export function migrateQuizCreatorDraft(
  userId: string | undefined,
  fromContextId: string | undefined,
  toContextId: string | undefined,
): void {
  if (!hasStorage() || !userId || !fromContextId || !toContextId || fromContextId === toContextId) return;
  const fromKey = getQuizCreatorDraftKey(userId, fromContextId);
  const raw = window.localStorage.getItem(fromKey);
  if (!raw) return;
  const toKey = getQuizCreatorDraftKey(userId, toContextId);
  try {
    window.localStorage.setItem(toKey, raw);
    window.localStorage.removeItem(fromKey);
  } catch {
    // Leave the old draft intact if the migration cannot be written.
  }
}

export function clearQuizCreatorDraftsForUser(userId: string | undefined): void {
  if (!hasStorage() || !userId) return;
  for (const key of listDraftKeysForUser(userId)) {
    window.localStorage.removeItem(key);
  }
  window.localStorage.removeItem(getClientContextKey(userId));
}

export function getOrCreateQuizCreatorClientContextId(userId: string | undefined): string | undefined {
  if (!hasStorage() || !userId) return undefined;
  const key = getClientContextKey(userId);
  const existing = window.localStorage.getItem(key);
  if (existing) return existing;
  const generated =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `client_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  window.localStorage.setItem(key, generated);
  return generated;
}
